import React from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button, Cell, Row, SectionHeader, Status, StatusPayment, Text, TooltipIcon, TooltipWrapper, User } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { IPayout } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { formatDateAndTime } from 'utils/date';
import { payoutStatusAlertMessage } from 'utils/payout';

// Payments Tab
export const ChartSectionHeader = styled(SectionHeader)`
  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: ${toRem(44)};
  }
`;

export const BusinessDayLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;

  > * {
    &:first-child {
      margin-bottom: ${toRem(8.25)};
    }
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row nowrap;
    align-items: center;

    > * {
      &:first-child {
        margin-right: ${toRem(11)};
        margin-bottom: 0;
      }
    }
  }
`;

export const BusinessDayLabel = styled(Text).attrs({
  as: 'label',
})`
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.medium};
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    font-size: ${toRem(13.25)};
    display: block;
  }
`;

export const IconContainer = styled.div`
  position: relative;
  justify-content: center;
  align-items: center;
  width: ${toRem(60)};
  height: ${toRem(60)};
  background-color: ${theme.color.secondary[400]};
  box-shadow: 0 0 0 ${toRem(15)} ${theme.color.secondary[25]};
  border-radius: 50%;
  margin-bottom: ${toRem(32)};
`;

const TotalsLayout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: center;

  .tooltip {
    display: flex;
    flex-flow: column nowrap;
  }
`;

const Total = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  margin-bottom: ${toRem(2)};

  .title {
    min-width: ${toRem(64)};
    font-weight: ${theme.font.weight.bold};
  }
`;

// PSR = Payment Set Row
const PSRRow = styled(Row)`
  height: ${toRem(106)};
`;

const PSRContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(5.5)};
  width: 100%;

  > * {
    margin-bottom: 0;
    line-height: 100%;
    color: ${theme.color.neutral[900]};
  }
`;

interface IPaymentSetProps {
  setID?: string;
  businessDay?: string;
  submittedDate?: string;
  submittedTime?: string;
  sentBy?: string;
  description?: string;
  statusPaid?: number;
  statusPending?: number;
  statusExpired?: number;
  statusCanceled?: number;
  statusPayroll?: number;
  totals?: { gross: number; holdbacks: number; net: number };
}

interface IPaymentSetRowProps extends IPaymentSetProps {
  isLoading?: boolean;
  onClick?: () => void;
}

export const PaymentSetRow = ({
  setID,
  businessDay,
  submittedDate,
  submittedTime,
  sentBy,
  description,
  statusPaid = 0,
  statusPending = 0,
  statusExpired = 0,
  statusCanceled = 0,
  statusPayroll = 0,
  totals,
  isLoading,
  onClick,
  ...rest
}: IPaymentSetRowProps) => {
  const totalPayees = statusPaid + statusPending + statusExpired + statusCanceled + statusPayroll;

  return (
    <PSRRow clickable onClick={onClick} {...rest}>
      <Cell data-cy="business-day-cell">
        <PSRContentLayout>
          <TooltipWrapper>
            {isLoading ? (
              <Skeleton width={100} />
            ) : (
              <>
                <strong>{businessDay}</strong>
                <TooltipIcon iconMarginLeft={5.5} id={setID}>
                  <p>
                    <strong>Date Submitted</strong>
                    <br />
                    {submittedDate}
                    <br />
                    {submittedTime}
                  </p>
                </TooltipIcon>
              </>
            )}
          </TooltipWrapper>

          <Text size="small" as="span" data-cy="payment-set-total-payees">
            {isLoading ? <Skeleton width={50} /> : `${totalPayees} ${totalPayees === 1 ? 'Payee' : 'Payees'}`}
          </Text>
        </PSRContentLayout>
      </Cell>

      <Cell align="right" data-cy="net-cell">
        {isLoading || !totals ? (
          <Skeleton width={75} />
        ) : (
          <TotalsLayout>
            <strong>{centsToDollarsDisplay(totals.net)}</strong>
            <TooltipIcon iconMarginLeft={5.5} id={`totals_${setID}`} className="tooltip">
              <Total>
                <div className="title">{'Gross: '}</div> {centsToDollarsDisplay(totals.gross)}
              </Total>
              <Total>
                <div className="title">{'Holdbacks: '}</div> {centsToDollarsDisplay(totals.holdbacks)}
              </Total>
              <Total>
                <div className="title">{'Net: '}</div> {centsToDollarsDisplay(totals.net)}
              </Total>
            </TooltipIcon>
          </TotalsLayout>
        )}
      </Cell>

      <Cell>
        <PSRContentLayout>
          {statusPaid !== 0 && !isLoading && <Status data-cy="status-paid" appearance={Appearance.SUCCESS} label={`Paid (${statusPaid})`} isLoading={isLoading} />}
          {statusPending !== 0 && !isLoading && <Status data-cy="status-pending" appearance={Appearance.WARNING} label={`Pending (${statusPending})`} isLoading={isLoading} />}
          {statusExpired !== 0 && !isLoading && <Status data-cy="status-expired" appearance={Appearance.DANGER} label={`Expired (${statusExpired})`} isLoading={isLoading} />}
          {statusCanceled !== 0 && !isLoading && <Status data-cy="status-canceled" appearance={Appearance.NEUTRAL} label={`Canceled (${statusCanceled})`} isLoading={isLoading} />}
          {statusPayroll !== 0 && !isLoading && <Status data-cy="status-payroll" appearance={Appearance.NEUTRAL} label={`Payroll (${statusPayroll})`} isLoading={isLoading} />}
        </PSRContentLayout>
      </Cell>

      <Cell data-cy="sent-by-cell">{isLoading ? <Skeleton width={100} /> : sentBy}</Cell>

      <Cell>
        <Text size="small" as="span" data-cy="payment-set-id">
          {isLoading ? <Skeleton width={100} /> : setID}
        </Text>
      </Cell>

      <Cell minWidth={300} maxWidth={350} wrapText data-cy="description-cell">
        {isLoading ? <Skeleton width={200} /> : description || '--'}
      </Cell>
    </PSRRow>
  );
};

// Details/Transactions Tab
export const DetailsContentHeader = styled(SectionHeader).attrs({
  afterHeaderCardRow: true,
})`
  padding: 0 ${toRem(theme.layout.gutter.small)};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.layout.gutter.large)};
  }
`;

interface ITransactionRowProps {
  payout?: IPayout;
  isLoading?: boolean;
  onClick?: () => void;
}

export const TransactionRow = ({ payout, isLoading, onClick, ...rest }: ITransactionRowProps) => (
  <Row clickable onClick={onClick} {...rest} data-value={payout?.id}>
    <Cell data-cy="payee-cell">
      <User id={payout?.id || ''} username={payout?.employee.name || ''} userId={payout?.userId} details={payout?.id} isLoading={isLoading} isDismissed={!!payout?.employee.deletedAt} />
    </Cell>

    <Cell data-cy="status-cell">
      <StatusPayment
        status={payout?.status}
        isLoading={isLoading}
        paymentId={payout?.id}
        {...(alert && {
          subStatusLabel: payout?.alert?.label,
          subStatusTooltip: payout?.alert?.message,
        })}
      />
    </Cell>

    <Cell data-cy="business-day-cell">
      <TooltipWrapper>
        {isLoading ? (
          <Skeleton width={100} />
        ) : (
          <>
            {payout?.paymentDate}
            <TooltipIcon iconMarginLeft={7} id={payout?.id}>
              <p>
                <strong>Date Submitted</strong>
                <br />
                {dayjs(payout?.createdAt).format('YYYY-MM-DD')}
                <br />
                {dayjs(payout?.createdAt).format('hh:mm:ss A z')}
              </p>
            </TooltipIcon>
          </>
        )}
      </TooltipWrapper>
    </Cell>

    <Cell align="left" data-cy="tip-amount-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay((payout?.gross || 0) - (payout?.autoGratuity || 0))}</strong>}
    </Cell>

    <Cell align="left" data-cy="autograts-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay(payout?.autoGratuity)}</strong>}
    </Cell>

    <Cell align="left" data-cy="gross-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay(payout?.gross)}</strong>}
    </Cell>

    <Cell align="left" data-cy="holdback-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToNegativeDollarsDisplay(payout?.holdback)}</strong>}
    </Cell>

    <Cell align="left" data-cy="paid-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay(payout?.net)}</strong>}
    </Cell>
  </Row>
);

interface IReviewRowProps {
  payout?: IPayout;
  isLoading?: boolean;
  currentEmployeeId?: string;
  onClick?: () => void;
}

export const ReviewRow = ({ payout, isLoading, currentEmployeeId, onClick }: IReviewRowProps) => {
  const sentBy = payout?.statusHistory?.find((status) => status.action === 'create')?.changedBy;
  const isSender = sentBy?.id === currentEmployeeId;
  const isReceiver = payout?.employee.id === currentEmployeeId;

  return (
    <Row data-value={payout?.id} data-cy="payment-review-row">
      <Cell data-cy="payout-id-cell">{isLoading ? <Skeleton width={100} /> : <strong>{payout?.id}</strong>}</Cell>

      <Cell data-cy="payee-cell">
        {isLoading ? (
          <Skeleton width={100} />
        ) : (
          <TooltipWrapper>
            {payout?.employee.name || '--'}
            {isReceiver && (
              <TooltipIcon place="top" iconMarginLeft={5.5} id={`receiver_disabled_${payout?.id}`} dataCy="receiver-disabled-tooltip">
                You can't review payments you were sent.
              </TooltipIcon>
            )}
          </TooltipWrapper>
        )}
      </Cell>

      <Cell data-cy="sent-by-cell">
        {isLoading ? (
          <Skeleton width={100} />
        ) : (
          <TooltipWrapper>
            {sentBy?.name || '--'}
            {isSender && (
              <TooltipIcon place="top" iconMarginLeft={5.5} id={`sender_disabled_${payout?.id}`} dataCy="sender-disabled-tooltip">
                You can't review payments you sent.
              </TooltipIcon>
            )}
          </TooltipWrapper>
        )}
      </Cell>

      <Cell data-cy="date-cell">{isLoading ? <Skeleton width={100} /> : payout?.createdAt ? formatDateAndTime(payout?.createdAt) : '--'}</Cell>

      <Cell data-cy="reason-cell">{isLoading ? <Skeleton width={100} /> : payoutStatusAlertMessage(payout) || '--'}</Cell>

      <Cell data-cy="amount-cell">{isLoading ? <Skeleton width={100} /> : payout?.net ? centsToDollarsDisplay(payout?.net) : '--'}</Cell>

      <Cell data-cy="review-cell">{isLoading ? <Skeleton width={100} /> : <Button data-cy="review-button" label="Review" type="button" appearance="primary" disabled={isSender || isReceiver} size="small" onClick={onClick} />}</Cell>
    </Row>
  );
};
